import { useEffect, useState } from 'react';
import { getIsMobileWrapperFlag } from '~shared/utils/user';

import { baseClassWithNavModifierClass } from './classNames';

/**
 * Helper function for checkNavElementsVisibility() - Update CSS for elements based on visibility
 * @param {DOMElement} maskEl the mask element (left or right)
 * @param {DOMElement} navEl the navigation container
 * @param {Boolean} visible left or right nav element visibility
 * @returns {Void} just void
 */
 function setNavLeftRightElementsVisibility(maskEl, navEl, visible = true) {
     // For now we leave mask for mobile screen. When we can use intersectionObserver to detect
     // scrolling extremeties. that will allow us to dynamically set the l/r masks on mobile
     // and to add mouse drag/touch to desktop view without using expensive DOM calls
     if (!visible && !getIsMobileWrapperFlag()) {
        maskEl.classList.add('hidden');
        navEl.classList.add('disabled');

        return;
    }

    maskEl.classList.remove('hidden');
    navEl.classList.remove('disabled');
}

/**
 * Check if nav DOM element visibility needs changing
 * @param {Object} navVisibilityRef React ref with current visibility settings - see updateNavVisibility()
 * @param {Object} carouselRef React Ref - carousel DOM element
 * @returns {Void} zip
 */
function checkNavElementsVisibility(navVisibilityRef, carouselRef) {
    const navVisibility = navVisibilityRef.current;
    carouselRef.current.classList.toggle(baseClassWithNavModifierClass, navVisibility.global);
    const maskEls = carouselRef.current.querySelectorAll('[data-mask]');
    maskEls.forEach((maskEl) => {
        const leftOrRight = maskEl.dataset.mask;
        const navEl = carouselRef.current.querySelector(`[data-arrow-direction="${leftOrRight}"]`);
        setNavLeftRightElementsVisibility(maskEl, navEl, navVisibility[leftOrRight]);
    });
}

/**
 * Update the navVisibilityRef object (e.g. after the scroller has been resized or scrolled)
 * NB: This function mutates the navVisibility item. Don't try to mutate it yourself.
 * @param {Object} param0 relevant dimensions to determine visibility
 * @param {Object} navVisibilityRef React ref with current visibility settings:
 *  navVisibilityRef.current => { left, right, global } booleans to flag if elements should be displayed
 * @returns {Boolean} If visibility was changed
 */
function updateNavVisibility({
    leftOffset, contentWidth, containerWidth,
}, navVisibilityRef) {
    const { left, right, global } = navVisibilityRef.current;
    const changedVisibility = {};
    const newLeftVisibility = leftOffset > 0;
    const newRightVisibility = contentWidth > (leftOffset + containerWidth);
    const newGlobalVisibility = !getIsMobileWrapperFlag() && contentWidth > containerWidth;
    if (left !== newLeftVisibility) {
        changedVisibility.left = newLeftVisibility;
    }
    if (right !== newRightVisibility) {
        changedVisibility.right = newRightVisibility;
    }
    if (global !== newGlobalVisibility) {
        changedVisibility.global = newGlobalVisibility;
    }

    const visibilityDidChange = Boolean(Object.keys(changedVisibility).length);
    if (visibilityDidChange) {
        navVisibilityRef.current = { ...navVisibilityRef.current, ...changedVisibility };
    }

    return visibilityDidChange;
}

/**
 * Update the nav visibility and elements
 * @param {Object} scrollDimensions relevant dimensions to determine visibility
 * @param {Object} carouselRef React Ref - carousel DOM element
 * @param {Object} navVisibilityRef React ref with current visibility settings
 * @returns {Void} void
 */
export function updateNavigation(scrollDimensions, carouselRef, navVisibilityRef) {
    const didUpdate = updateNavVisibility(scrollDimensions, navVisibilityRef);
    if (didUpdate) {
        checkNavElementsVisibility(navVisibilityRef, carouselRef);
    }
}

/**
 * Get a prop representing a gtm attribute for a carousel item
 * !! important !! if passing slug as an extra attr remember to pass
 * only 2 params in first array otherwise it will not update the DOM
 * with data-spectate-product attribute
 * @param {Array} baseAttributes array of strings [main component, (optional) secondary component]
 * @param {String} extraAttribute possible third level item
 * @returns {Object} suitable to pass as a spread prop, e.g. { dataAttribute: 'one;two;three' } or {}
 */
export function getGtmAttributeProp(baseAttributes = [], extraAttribute = '') {
    if (!baseAttributes.length) {

        return {};
    }

    const gtmAttributes = [ ...baseAttributes ];

    if (extraAttribute) {
        gtmAttributes.push(extraAttribute);
    }

    return { 'data-spectate-product': gtmAttributes.join(';') };
}

export const useCarouselLayoutUpdate = (conditions = []) => {
    const [doForceRedraw, setDoForceRedraw] = useState(false);

    useEffect(() => {
        setDoForceRedraw(true);
        setTimeout(() => setDoForceRedraw(false), 0);
    }, conditions);

    return doForceRedraw;
};
